import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'
import Button from '../../ui/primitives/Button'
import { useSession } from '../../app/providers/sessionContext'

/**
 * The pages a signed-in user sees when they cannot reach what they asked for.
 *
 * Each one says what is true, what the person can do about it, and nothing
 * else. No table name, no policy name, no Supabase error, no user id. A person
 * locked out of a screen cannot act on a Postgres error code, and printing one
 * only tells an attacker how the inside is arranged.
 */

function AccessFrame({
  title,
  children,
  actions,
  testId,
}: {
  title: string
  children: ReactNode
  actions?: ReactNode
  testId: string
}) {
  return (
    <main
      data-testid={testId}
      className="mx-auto flex min-h-[60vh] w-full max-w-xl flex-col justify-center px-4 py-10"
    >
      <div className="rounded-[10px] border border-line bg-surface px-6 py-7">
        <h1 className="text-[1.5rem] font-bold uppercase tracking-wide text-steel-2">
          {title}
        </h1>
        <div className="mt-3 space-y-3 text-[0.95rem] leading-relaxed text-ink">{children}</div>
        {actions !== undefined && <div className="mt-6 flex flex-wrap gap-3">{actions}</div>}
      </div>
    </main>
  )
}

/** Shown while the session is being resolved. Announced, not just spun. */
export function AuthLoading() {
  return (
    <main
      data-testid="auth-loading"
      role="status"
      aria-live="polite"
      className="mx-auto flex min-h-[60vh] w-full max-w-xl items-center justify-center px-4"
    >
      <p className="text-[0.95rem] font-semibold uppercase tracking-wide text-ink-muted">
        Checking your sign-in
      </p>
    </main>
  )
}

export function NoMembership() {
  const { email, isPlatformAdmin, signOut } = useSession()
  const navigate = useNavigate()

  /*
   * A platform administrator belongs to no client organisation by design, so
   * this page is a correct description of their account and a dead end for
   * their job. Without a way out it offers one action, Sign out, on the screen
   * they land on after signing in.
   *
   * This grants nothing. The platform route still checks `isPlatformAdmin`
   * itself, against platform_admins, and the database refuses regardless. All
   * this does is stop the product hiding a door from somebody holding the key.
   */
  return (
    <AccessFrame
      testId="state-no-membership"
      title="No organisation yet"
      actions={
        <>
          {isPlatformAdmin && (
            <Button
              variant="primary"
              data-testid="no-membership-open-platform"
              onClick={() => navigate('/platform/organisations')}
            >
              Open platform administration
            </Button>
          )}
          <Button
            variant={isPlatformAdmin ? 'secondary' : 'primary'}
            onClick={() => void signOut()}
          >
            Sign out
          </Button>
        </>
      }
    >
      <p>
        You are signed in{email === null ? '' : ` as ${email}`}, but your account is not part of an
        organisation yet.
      </p>
      {isPlatformAdmin ? (
        <p>
          Your account is a platform administrator, which is not a membership of any client
          organisation. Platform administration is where you provision one.
        </p>
      ) : (
        <p>
          Access is granted by invitation. If you were expecting to be added, ask the person who
          invited you to check that the invitation was sent to this address.
        </p>
      )}
    </AccessFrame>
  )
}

export function OrganisationSuspended() {
  const { membership, signOut } = useSession()
  return (
    <AccessFrame
      testId="state-organisation-suspended"
      title="Organisation suspended"
      actions={
        <Button variant="ghost" onClick={() => void signOut()}>
          Sign out
        </Button>
      }
    >
      <p>
        {membership === null ? 'This organisation' : membership.organisationName} is suspended, so
        it is read-only for everyone.
      </p>
      <p>Nothing has been deleted. Contact SSDN Consulting to have it reactivated.</p>
    </AccessFrame>
  )
}

export function PermissionDeniedPage() {
  return (
    <AccessFrame
      testId="state-permission-denied"
      title="You do not have access to this"
      actions={
        <Button variant="ghost" onClick={() => window.history.back()}>
          Go back
        </Button>
      }
    >
      <p>Your role in this organisation does not include this section.</p>
      <p>If you need it, an owner or administrator can change your access.</p>
    </AccessFrame>
  )
}

export function SessionExpired() {
  return (
    <AccessFrame
      testId="state-session-expired"
      title="Your session has ended"
      actions={
        <Button variant="primary" onClick={() => window.location.assign('/login')}>
          Sign in again
        </Button>
      }
    >
      <p>You were signed out because your session expired.</p>
      <p>Signing in again will take you back to where you were.</p>
    </AccessFrame>
  )
}

/**
 * Access resolution failed. Deliberately does not distinguish a policy refusal
 * from a network problem on screen: the user's next step is the same, and the
 * difference is only useful to somebody probing the system.
 */
export function AccessResolutionError() {
  const { refreshAccess, signOut } = useSession()
  return (
    <AccessFrame
      testId="state-access-error"
      title="We could not load your access"
      actions={
        <>
          <Button variant="primary" onClick={() => void refreshAccess()}>
            Try again
          </Button>
          <Button variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
        </>
      }
    >
      <p>Something went wrong while checking what you can see. This is usually temporary.</p>
      <p>If it keeps happening, contact SSDN Consulting.</p>
    </AccessFrame>
  )
}
